import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { fetchChargerList, fetchSummaryData, fetchConnectorHistory, fetchConnectorHistoryForCharger, fetchAllSessionDataForChargers, fetchChargerStatusForErrorTab, fetchSiteSessionData } from '../services/chargerService';
import type { SessionData, Connector2 } from '../types/chargerTypes';
import compact from 'lodash/compact';
import map from 'lodash/map';
import type { LiveDataItem } from '../types/powerTypes';

// Define the ChargerModel and ChargerData types
interface ChargerModel {
    id: number;
    vendor: string;
    model: string;
    capacity: number;
    charger_type: string;
    connectors: Connector2;
    image_src: string;
    charger_link: string;
}

interface ChargerData {
    id: number;
    charger_title: string;
    charger_model: ChargerModel | any;
    serial_number: string;
    station_id: string;
    station_name: string;
    api_source: string;
    extra_data: Record<string, unknown>;
}

// Define the ChargerState type
interface ChargerState {
    chargers: ChargerData[];
    dataTobeDis: any,
    sessionTabIndex: string;
    chargersId: number[];
    liveDataAcrossSites: LiveDataItem[];
    summary: SessionData[];
    connectorHistory: any[];
    connectorHistoryForCharger: any[];
    sessionData: SessionData[]; // New state for session data
    siteSessionData: SessionData[];
    isLoadingChargerData: boolean;
    fetchingChargerDataForErrorTab: boolean;
    isLoadingConnectorHistoryForCharger: boolean;
    errorTabChargers: number[];
    isLoadingSummary: boolean;
    isLoadingConnectorHistory: boolean;
    isLoadingSessionData: boolean; // New loading state for session data
    isLoadingSiteSessionData: boolean;
    error: string | null;
    errorCharger: string | null;
}

// Initialize the state
const initialState: ChargerState = {
    chargers: [],
    errorCharger: '',
    liveDataAcrossSites: [],
    dataTobeDis: {},
    sessionTabIndex: '1',
    chargersId: [],
    errorTabChargers: [],
    summary: [],
    connectorHistory: [],
    connectorHistoryForCharger: [],
    sessionData: [], // Initialize session data state
    siteSessionData: [],
    isLoadingChargerData: false,
    isLoadingSummary: false,
    isLoadingConnectorHistory: false,
    isLoadingConnectorHistoryForCharger: false,
    fetchingChargerDataForErrorTab: false,
    isLoadingSessionData: false, // Initialize loading state
    isLoadingSiteSessionData: false,
    error: null,
};

// Create the slice
const chargerSlice = createSlice({
    name: 'charger',
    initialState,
    reducers: {
        setSessionTabIndex(state, action) {
            state.sessionTabIndex = action.payload
        },
        clearChargerData() {
            return initialState
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchChargerList.pending, (state) => {
                state.isLoadingChargerData = true;
                state.errorCharger = null;
            })
            .addCase(fetchChargerList.fulfilled, (state, action: PayloadAction<ChargerData[]>) => {
                state.isLoadingChargerData = false;
                state.chargers = action.payload;
                state.errorCharger = action.payload.length === 0 ? 'No chargers available' : ''
                state.chargersId = compact(map(action.payload, "id"));
            })
            .addCase(fetchChargerList.rejected, (state, action) => {
                if (action.payload && (action.payload === 'Request canceled')) {
                    state.isLoadingSessionData = true;
                }
                else state.isLoadingSessionData = false;
                state.errorCharger = action.error.message || 'Failed to fetch charger list';
            })
            .addCase(fetchSummaryData.pending, (state) => {
                state.isLoadingSummary = true;
                state.error = null;
            })
            .addCase(fetchSummaryData.fulfilled, (state, action: PayloadAction<SessionData[]>) => {
                state.isLoadingSummary = false;
                state.summary = action.payload;
            })
            .addCase(fetchSummaryData.rejected, (state, action: any) => {
                if (action.payload.canceled) {
                    state.isLoadingSummary = true; // Correct handling of aborted action
                } else {
                    state.isLoadingSummary = false;
                    state.error = action.error.message || 'Failed to fetch summary data';
                }
            })
            .addCase(fetchConnectorHistory.pending, (state) => {
                state.isLoadingConnectorHistory = true;
                state.error = null;
            })
            .addCase(fetchConnectorHistory.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.isLoadingConnectorHistory = false;
                state.connectorHistory = action.payload;
            })
            .addCase(fetchConnectorHistory.rejected, (state, action: any) => {
                if (action.payload?.canceled) {
                    state.isLoadingConnectorHistory = true;
                } else {
                    state.isLoadingConnectorHistory = false;
                    state.error = action.error?.message || 'Failed to fetch connector history';
                }
            })
            .addCase(fetchConnectorHistoryForCharger.pending, (state) => {
                state.isLoadingConnectorHistoryForCharger = true;
                state.error = null;
            })
            .addCase(fetchConnectorHistoryForCharger.fulfilled, (state, action: PayloadAction<any[]>) => {
                state.isLoadingConnectorHistoryForCharger = false;
                state.connectorHistoryForCharger = action.payload;
            })
            .addCase(fetchConnectorHistoryForCharger.rejected, (state, action: any) => {
                if (action.payload?.canceled) {
                    state.isLoadingConnectorHistoryForCharger = true;
                } else {
                    state.isLoadingConnectorHistoryForCharger = false;
                    state.error = action.error?.message || 'Failed to fetch connector history for charger';
                }
            })
            // Handle fetchAllSessionDataForChargers actions
            .addCase(fetchAllSessionDataForChargers.pending, (state) => {
                state.isLoadingSessionData = true; // Loading starts
                state.error = null; // Reset error
            })
            // Fulfilled: Set loading to false and store the fetched data
            .addCase(fetchAllSessionDataForChargers.fulfilled, (state, action: PayloadAction<SessionData[]>) => {
                state.isLoadingSessionData = false; // Loading ends
                state.sessionData = action.payload; // Store session data
            })
            // Rejected: Set loading to false and store the error message
            .addCase(fetchAllSessionDataForChargers.rejected, (state, action) => {
                // state.isLoadingSessionData = false; // Loading ends
                state.error = action.payload || 'Failed to fetch session data'; // Store the error
                if (action.payload && (action.payload === 'Request canceled')) {
                    state.isLoadingSessionData = true;
                }
                else state.isLoadingSessionData = false;
            }).addCase(fetchChargerStatusForErrorTab.pending, (state) => {
                state.fetchingChargerDataForErrorTab = true;
            }).addCase(fetchChargerStatusForErrorTab.rejected, (state, action) => {
                state.fetchingChargerDataForErrorTab = false;
                state.dataTobeDis = initialState.dataTobeDis;
                state.error = action.error.message || 'Failed to fetch charger status';
            }).addCase(fetchChargerStatusForErrorTab.fulfilled, (state, payload) => {
                state.dataTobeDis = payload.payload
                state.fetchingChargerDataForErrorTab = false;
            })
            // Handle fetchSiteSessionData actions
            .addCase(fetchSiteSessionData.pending, (state) => {
                state.isLoadingSiteSessionData = true;
                state.error = null;
            })
            .addCase(fetchSiteSessionData.fulfilled, (state, action: PayloadAction<SessionData[]>) => {
                state.isLoadingSiteSessionData = false;
                state.siteSessionData = action.payload;
            })
            .addCase(fetchSiteSessionData.rejected, (state, action) => {
                if (action.payload && action.payload === 'Request canceled') {
                    state.isLoadingSiteSessionData = true;
                } else {
                    state.isLoadingSiteSessionData = false;
                    state.error = action.payload || 'Failed to fetch site session data';
                }
            })
    },
});

export const { clearChargerData, setSessionTabIndex } = chargerSlice.actions;

export default chargerSlice.reducer;
